import { useEffect, useRef, useCallback } from 'react'
import { create } from 'zustand'

// ═══ Music Player Store (Zustand — persists across navigation) ═══

interface Track {
  title: string
  artist: string
  scUrl: string
  art?: string
  rec?: string
  note?: string
}

interface MusicState {
  playlist: Track[]
  currentIndex: number
  isPlaying: boolean
  duration: number
  position: number
  visible: boolean
  volume: number
  speed: number
  setPlaylist: (playlist: Track[]) => void
  playTrack: (index: number) => void
  play: () => void
  pause: () => void
  next: () => void
  prev: () => void
  hide: () => void
  setPosition: (pos: number) => void
  setDuration: (dur: number) => void
  setIsPlaying: (playing: boolean) => void
  setVolume: (vol: number) => void
  setSpeed: (speed: number) => void
}

export const useMusicStore = create<MusicState>((set, get) => ({
  playlist: [],
  currentIndex: -1,
  isPlaying: false,
  duration: 0,
  position: 0,
  visible: false,
  volume: 80,
  speed: 1,
  setPlaylist: (playlist) => set({ playlist }),
  playTrack: (index) => {
    const { playlist } = get()
    if (index < 0 || index >= playlist.length) return
    set({ currentIndex: index, isPlaying: true, visible: true, duration: 0, position: 0 })
  },
  play: () => set({ isPlaying: true }),
  pause: () => set({ isPlaying: false }),
  next: () => {
    const { currentIndex, playlist } = get()
    if (currentIndex < playlist.length - 1) get().playTrack(currentIndex + 1)
  },
  prev: () => {
    const { currentIndex } = get()
    if (currentIndex > 0) get().playTrack(currentIndex - 1)
  },
  hide: () => set({ visible: false, isPlaying: false }),
  setPosition: (pos) => set({ position: pos }),
  setDuration: (dur) => set({ duration: dur }),
  setIsPlaying: (playing) => set({ isPlaying: playing }),
  setVolume: (vol) => set({ volume: vol }),
  setSpeed: (speed) => set({ speed }),
}))

// Expose globally for community pages that use onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).npSetPlaylist = (playlist: Track[]) => {
    useMusicStore.getState().setPlaylist(playlist)
  };
  (window as unknown as Record<string, unknown>).npPlayTrack = (index: number) => {
    useMusicStore.getState().playTrack(index)
  }
}

// Scope music state by user session to prevent cross-community bleed
function getNpKey(): string {
  try {
    const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
    if (!token) return 'np_state'
    const payload = JSON.parse(atob(token.split('.')[1]))
    return `np_state_${payload.communitySlug || payload.userId || 'default'}`
  } catch { return 'np_state' }
}

function formatTime(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec < 10 ? '0' : ''}${sec}`
}

// ═══ Music Player Component (lives in Layout — never unmounts) ═══

export default function MusicPlayer() {
  const { playlist, currentIndex, isPlaying, duration, position, visible, volume } = useMusicStore()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const widgetRef = useRef<SC.SoundCloudWidget | null>(null)
  const timerRef = useRef<number | null>(null)
  const scLoadedRef = useRef(false)

  const track = currentIndex >= 0 && currentIndex < playlist.length ? playlist[currentIndex] : null

  // Load SoundCloud Widget API once
  useEffect(() => {
    if (scLoadedRef.current) return
    const script = document.createElement('script')
    script.src = 'https://w.soundcloud.com/player/api.js'
    script.onload = () => { scLoadedRef.current = true }
    document.head.appendChild(script)
  }, [])

  // Play track when currentIndex changes
  useEffect(() => {
    if (!track || !iframeRef.current) return

    iframeRef.current.src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(track.scUrl)}&auto_play=true&show_artwork=false&visual=false`

    function initWidget() {
      if (typeof SC === 'undefined' || !SC.Widget) {
        setTimeout(initWidget, 100)
        return
      }
      const widget = SC.Widget(iframeRef.current!)
      widgetRef.current = widget

      widget.bind(SC.Widget.Events.READY, () => {
        widget.getDuration((d: number) => {
          if (d > 0) useMusicStore.getState().setDuration(d)
        })
        widget.setVolume(80)

        if (timerRef.current) clearInterval(timerRef.current)
        timerRef.current = window.setInterval(() => {
          const state = useMusicStore.getState()
          if (!state.isPlaying) return
          widget.getDuration((d: number) => {
            if (d > 0) useMusicStore.getState().setDuration(d)
          })
          widget.getPosition((pos: number) => {
            useMusicStore.getState().setPosition(pos)
            // Save to localStorage
            localStorage.setItem(getNpKey(), JSON.stringify({
              playlist: state.playlist,
              index: state.currentIndex,
              position: pos,
              wasPlaying: state.isPlaying,
            }))
          })
        }, 500)
      })

      widget.bind(SC.Widget.Events.PLAY, () => useMusicStore.getState().setIsPlaying(true))
      widget.bind(SC.Widget.Events.PAUSE, () => useMusicStore.getState().setIsPlaying(false))
      widget.bind(SC.Widget.Events.FINISH, () => useMusicStore.getState().next())
    }
    initWidget()

    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [currentIndex, track?.scUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore saved state on mount
  useEffect(() => {
    const saved = localStorage.getItem(getNpKey())
    if (!saved) return
    try {
      const state = JSON.parse(saved)
      if (!state.playlist?.length) return
      useMusicStore.getState().setPlaylist(state.playlist)
      setTimeout(() => useMusicStore.getState().playTrack(state.index), 500)
    } catch { /* ignore */ }
  }, [])

  // Sync volume when it changes
  useEffect(() => {
    if (widgetRef.current) widgetRef.current.setVolume(volume)
  }, [volume])

  const handlePlayPause = useCallback(() => {
    if (!widgetRef.current) return
    if (isPlaying) { widgetRef.current.pause() } else { widgetRef.current.play() }
  }, [isPlaying])

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!widgetRef.current || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const pct = (e.clientX - rect.left) / rect.width
    widgetRef.current.seekTo(pct * duration)
  }, [duration])

  const pct = duration > 0 ? (position / duration) * 100 : 0

  if (!visible || !track) return (
    <iframe ref={iframeRef} style={{ display: 'none' }} allow="autoplay" title="SoundCloud" />
  )

  return (
    <>
      <iframe ref={iframeRef} style={{ display: 'none' }} allow="autoplay" title="SoundCloud" />
      <div
        className="shrink-0 flex items-center gap-3 px-4"
        style={{
          height: 64,
          background: 'linear-gradient(to top, #080E1A, #0D1526)',
          borderTop: '1px solid rgba(212,160,84,0.15)',
        }}
      >
        {/* Art */}
        {track.art && (
          <img src={track.art} alt="" className="rounded-md object-cover shrink-0" style={{ width: 44, height: 44 }} />
        )}

        {/* Info */}
        <div className="w-44 shrink-0 min-w-0">
          <div className="text-xs font-semibold truncate" style={{ color: '#F0F4F8' }}>{track.title}</div>
          <div className="text-xs truncate" style={{ color: '#7B8FA3' }}>{track.artist}</div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button onClick={() => useMusicStore.getState().prev()} className="text-base" style={{ background: 'none', border: 'none', color: '#F0F4F8', cursor: 'pointer' }}>⏮</button>
          <button onClick={handlePlayPause} className="text-lg" style={{ background: 'none', border: 'none', color: '#F0F4F8', cursor: 'pointer' }}>{isPlaying ? '⏸' : '▶'}</button>
          <button onClick={() => useMusicStore.getState().next()} className="text-base" style={{ background: 'none', border: 'none', color: '#F0F4F8', cursor: 'pointer' }}>⏭</button>
        </div>

        {/* Scrubber */}
        <span className="text-xs font-mono shrink-0" style={{ color: '#7B8FA3', width: 36 }}>{formatTime(position)}</span>
        <div
          className="flex-1 h-1 rounded-full cursor-pointer relative"
          style={{ background: 'rgba(255,255,255,0.08)' }}
          onClick={handleSeek}
        >
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--accent)', transition: 'width 0.1s linear' }} />
        </div>
        <span className="text-xs font-mono shrink-0" style={{ color: '#7B8FA3', width: 36, textAlign: 'right' }}>{formatTime(duration)}</span>

        {/* Volume */}
        <div className="flex items-center gap-1 shrink-0 ml-1">
          <span className="text-xs" style={{ color: '#7B8FA3' }}>🔊</span>
          <input
            type="range"
            min={0}
            max={100}
            value={volume}
            onChange={e => useMusicStore.getState().setVolume(Number(e.target.value))}
            className="w-16 h-1 accent-[var(--accent)]"
            style={{ cursor: 'pointer' }}
            title={`Volume: ${volume}%`}
          />
        </div>

        {/* Close */}
        <button onClick={() => useMusicStore.getState().hide()} className="text-sm ml-1" style={{ background: 'none', border: 'none', color: '#7B8FA3', cursor: 'pointer' }}>✕</button>
      </div>
    </>
  )
}

// TypeScript declarations for SoundCloud Widget API
declare namespace SC {
  function Widget(iframe: HTMLIFrameElement): SoundCloudWidget
  namespace Widget {
    enum Events { READY = 'ready', PLAY = 'play', PAUSE = 'pause', FINISH = 'finish' }
  }
  interface SoundCloudWidget {
    bind(event: string, callback: () => void): void
    play(): void
    pause(): void
    seekTo(ms: number): void
    setVolume(vol: number): void
    getDuration(callback: (d: number) => void): void
    getPosition(callback: (p: number) => void): void
  }
}
